import { create } from 'zustand';
import type { IUserInfo, IUserPermission } from '../../types/components';
import { logger } from '../middlewares/logger.middleware';

// ============================================================================
// Permission Action Type
// ============================================================================

export type PermissionAction = 'read' | 'create' | 'update' | 'delete' | '*';

// ============================================================================
// Auth State Interface
// ============================================================================

interface IAuthState {
  // User State
  userInfo: IUserInfo | null;
  retrying: boolean;

  // Permission State (stored as simple resource:action pairs)
  userPermissions: IUserPermission[];
  permissionVersion: string | null;

  // User Setters
  setUserInfo: (info: IUserInfo | null) => void;
  setRetrying: (retry: boolean) => void;

  // Permission Setters
  setUserPermissions: (permissions: IUserPermission[]) => void;
  setPermissionVersion: (version: string | null) => void;
  getPermissionVersion: () => string | null;

  // Permission Selectors
  hasPermission: (resourceName: string, action: PermissionAction) => boolean;
  hasAnyPermission: (resourceName: string) => boolean;
  canAccessResource: (resourceName: string) => boolean;

  // Combined Actions
  clearAuth: () => void;
}

// ============================================================================
// Store Implementation
// ============================================================================

const useAuthStore = create<IAuthState>()(
  logger(
    (set, get) => ({
      // Initial User State
      userInfo: null,
      retrying: false,

      // Initial Permission State
      userPermissions: [],
      permissionVersion: null,

      // User Setters
      setRetrying: retry => set(() => ({ retrying: retry })),
      setUserInfo: info => set(() => ({ userInfo: info })),

      // Permission Setters
      setUserPermissions: permissions =>
        set(() => ({ userPermissions: permissions })),
      setPermissionVersion: version =>
        set(() => ({ permissionVersion: version })),
      getPermissionVersion: () => get().permissionVersion,

      // Permission Selectors
      hasPermission: (
        resourceName: string,
        action: PermissionAction,
      ): boolean => {
        const { userPermissions } = get();

        // Check for wildcard permission (*:*) - superadmin gets this
        const hasWildcard = userPermissions.some(
          perm => perm.resource_name === '*' && perm.action === '*',
        );
        if (hasWildcard) return true;

        // Check for specific permission or resource wildcard
        return userPermissions.some(
          perm =>
            perm.resource_name === resourceName &&
            (perm.action === action || perm.action === '*'),
        );
      },

      hasAnyPermission: (resourceName: string): boolean => {
        const { userPermissions } = get();

        // Check for wildcard permission (*:*) - superadmin gets this
        const hasWildcard = userPermissions.some(
          perm => perm.resource_name === '*' && perm.action === '*',
        );
        if (hasWildcard) return true;

        // Check if user has any permission for this resource
        return userPermissions.some(
          perm => perm.resource_name === resourceName,
        );
      },

      canAccessResource: (resourceName: string): boolean => {
        // Alias for hasAnyPermission - checks if user can access (read) a resource
        return get().hasAnyPermission(resourceName);
      },

      // Combined Actions
      clearAuth: () =>
        set(() => ({
          userInfo: null,
          retrying: false,
          userPermissions: [],
          permissionVersion: null,
        })),
    }),
    'auth',
  ),
);

export default useAuthStore;
